/**
 * The sign-in screen (the console previously had none — it just said
 * "Authentication required" and left you there).
 *
 * There is no password form here on purpose. PLAYER is the platform's SSO surface,
 * its cookie is scoped to `.teachingmachines.se` so it is already valid on this
 * host, and it accepts `?return_to=` for any teachingmachines.se address. A second
 * login form would mean a second place handling passwords for no benefit.
 */
import { useEffect, useState } from 'react';

interface Props {
  signInUrl: string;
  /** Set when the person IS signed in but lacks the role. */
  refusal?: { email: string; role: string } | null;
}

export function SignIn({ signInUrl, refusal }: Props) {
  const [returning, setReturning] = useState(false);

  const target = `${signInUrl}?return_to=${encodeURIComponent(window.location.href)}`;

  // Enter signs in — this screen has exactly one action.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !refusal) { setReturning(true); window.location.href = target; }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [target, refusal]);

  return (
    <main className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <img src="/svg/logo-dark.svg" alt="Teaching Machines" className="mb-5 h-16 w-auto" />
          <h1 className="text-2xl font-semibold tracking-tight">QA Console</h1>
          <p className="mt-1.5 text-sm" style={{ color: 'hsl(var(--text-muted))' }}>
            Teaching Machines — test automation
          </p>
        </div>

        <div className="surface p-6">
          {refusal ? (
            <>
              <div className="pill pill-fail mb-4">Access denied</div>
              <p className="text-sm leading-relaxed">
                You are signed in as{' '}
                <span className="font-medium" style={{ color: 'hsl(var(--text))' }}>{refusal.email}</span>{' '}
                with the role{' '}
                <span className="font-medium" style={{ color: 'hsl(var(--text))' }}>{refusal.role}</span>.
              </p>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: 'hsl(var(--text-muted))' }}>
                The console can start runs against shared environments and read every
                organisation's test data, so it is limited to platform administrators.
              </p>
              <a href={target} className="btn btn-secondary mt-5 w-full" data-testid="switch-account">
                Sign in as someone else
              </a>
            </>
          ) : (
            <>
              <p className="text-sm leading-relaxed" style={{ color: 'hsl(var(--text-muted))' }}>
                Sign in with your Teaching Machines account. You will be taken to the
                player to authenticate, then brought straight back here.
              </p>
              <a
                href={target}
                onClick={() => setReturning(true)}
                className="btn btn-primary mt-5 w-full"
                data-testid="sign-in"
              >
                {returning ? 'Redirecting…' : 'Sign in'}
              </a>
              <p className="mt-4 text-center text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
                Platform administrators only
              </p>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
          Already signed in elsewhere? Your session is shared across
          teachingmachines.se — just press Sign in.
        </p>
      </div>
    </main>
  );
}
